<template>
	<div class="card">
		<div class="section-header section-header-action">
			<div>
				<h5 class="section-title">Actividades económicas</h5>
				<p class="section-desc">Cada actividad es un nombre comercial con su logo, que factura desde un punto de emisión.</p>
			</div>
			<Button label="Nueva actividad" icon="pi pi-plus" class="p-button-sm header-button" aria-label="Nueva actividad económica"
				:disabled="activitiesFull" @click="openNew" />
		</div>

		<Message v-if="activitiesFull" severity="warn" :closable="false" class="mt-0">
			Alcanzaste el número de actividades de tu plan. Agrega una actividad adicional por $15 desde
			<router-link to="/dashboard/general/suscripcion">Suscripción</router-link>.
		</Message>
		<small v-else-if="activityRemaining !== null" class="quota">
			Puedes registrar {{ activityRemaining }} actividad(es) más en tu plan.
		</small>

		<p v-if="loaded && !activities.length" class="empty">No hay actividades económicas registradas.</p>

		<ul class="act-list">
			<li v-for="activity in activities" :key="activity.id" class="act">
				<span class="act-logo-slot">
					<ActivityLogo :activity="activity" :size="40" imgClass="act-logo" :alt="'Logo de ' + activity.name" />
				</span>
				<div class="act-body">
					<div class="act-name">{{ activity.name }}</div>
					<div v-if="sourceOf(activity)" class="act-from">
						Factura desde <span class="series">{{ sourceOf(activity).point.series }}</span> ·
						{{ sourceOf(activity).establishment.label }} —
						<template v-if="hasAddress(sourceOf(activity).establishment)">{{ sourceOf(activity).establishment.address }}</template>
						<span v-else class="no-address">sin dirección</span>
					</div>
				</div>
				<Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-plain" v-tooltip.top="'Editar'"
					:aria-label="'Editar la actividad ' + activity.name" @click="openEdit(activity)" />
			</li>
		</ul>

		<Dialog v-model:visible="dialog" :header="editing ? 'Actividad económica' : 'Nueva actividad económica'" :modal="true"
			class="p-fluid" :style="{width: '38rem'}" :breakpoints="{'640px': '94vw'}" @after-hide="reset">
			<Message v-if="formError" severity="error" :closable="false">{{ formError }}</Message>
			<div class="field">
				<label for="activityName" class="field-label">Nombre comercial</label>
				<InputText id="activityName" v-model="name" maxlength="250" :class="{'p-invalid': errors.name}" aria-describedby="activityNameHelp" autofocus />
				<small v-if="errors.name" id="activityNameHelp" class="field-error" role="alert">{{ errors.name }}</small>
				<small v-else id="activityNameHelp" class="field-help">Sale como nombre comercial en tus comprobantes.</small>
			</div>

			<div class="field">
				<label for="activityPoint" class="field-label">Factura desde</label>
				<Dropdown inputId="activityPoint" v-model="choice" :options="options" optionLabel="label" optionValue="value"
					optionGroupLabel="label" optionGroupChildren="items" placeholder="Elige un punto de emisión"
					:class="{'p-invalid': errors.point}" aria-describedby="activityPointHelp" />
				<small v-if="errors.point" id="activityPointHelp" class="field-error" role="alert">{{ errors.point }}</small>
			</div>

			<Message v-if="movesNumbering" severity="warn" :closable="false">
				Desde que guardes, las facturas de <b>{{ editing.name }}</b> salen con la serie <span class="series">{{ chosen.point.series }}</span> y su numeración.
			</Message>
			<Message v-else-if="shared.length" severity="info" :closable="false">
				Compartirá la numeración con <b>{{ shared.join(', ') }}</b>: sus facturas siguen la misma serie <span class="series">{{ chosen.point.series }}</span>.
			</Message>
			<Message v-if="chosen && !hasAddress(chosen.establishment)" severity="error" :closable="false">
				El establecimiento {{ chosen.establishment.code }} no tiene dirección.
				<button type="button" class="inline-link" @click="fixEstablishment(chosen.establishment)">Complétala</button> antes de facturar.
			</Message>

			<div v-if="newPointIn" class="subform">
				<p class="subform-title">Nuevo punto en {{ newPointIn.code }} · {{ newPointIn.label }}</p>
				<EmissionFields v-model="draft" :errors="errors" mode="point" :establishmentCode="newPointIn.code" idPrefix="act-point" />
			</div>
			<div v-else-if="choice === NEW_ESTABLISHMENT" class="subform">
				<p class="subform-title">Nuevo establecimiento</p>
				<EmissionFields v-model="draft" :errors="errors" mode="establishment" idPrefix="act-estab" />
			</div>

			<div class="field mb-0">
				<span class="field-label">Logotipo</span>
				<div class="logo-pick">
					<span class="logo-frame">
						<img v-if="logoPreview" :src="logoPreview" alt="Logo elegido" class="logo-preview" />
						<ActivityLogo v-else :activity="logoActivity" :size="56" imgClass="logo-preview" alt="Logo actual" />
					</span>
					<div class="logo-actions">
						<Button label="Elegir imagen" icon="pi pi-image" class="p-button-outlined p-button-sm" @click="$refs.logoInput.click()" />
						<Button v-if="logoSource" label="Ajustar" icon="pi pi-arrows-alt" class="p-button-text p-button-sm" @click="cropperVisible = true" />
					</div>
					<input ref="logoInput" type="file" accept="image/*" class="hidden" @change="onLogoChanged" />
				</div>
				<small v-if="errors.logoFile" class="field-error" role="alert">{{ errors.logoFile }}</small>
				<small v-else class="field-help">PNG o JPG. Lo ajustas a 950 × 400 px antes de guardar. Sin logo, mostramos las iniciales.</small>
			</div>

			<template #footer>
				<Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="dialog = false" />
				<Button label="Guardar" icon="pi pi-check" class="p-button-success" :loading="saving" @click="save" />
			</template>
		</Dialog>

		<LogoCropper :visible="cropperVisible" :file="logoSource" @cropped="onCropped" @cancel="cropperVisible = false" />
	</div>
</template>

<script>
	import httpRequest from '../../service/httpRequest';
	import ActivityLogo from '../General/ActivityLogo.vue';
	import EmissionFields from './EmissionFields.vue';
	import LogoCropper from './LogoCropper.vue';
	import {
		activityPointFields, emptySequentials, findPoint, hasAddress, pointOptions, pointValue, serverField,
		sharedWith, validateNewEstablishment, validateNewPoint, NEW_ESTABLISHMENT,
	} from '../../service/establishments';

	const NETWORK_ERROR = 'No se pudo guardar. Revisa tu conexión e intenta de nuevo.';

	export default {
		name: 'EconActivitiesCard',
		components: { ActivityLogo, EmissionFields, LogoCropper },
		props: {
			activities: { type: Array, default: () => [] },
			establishments: { type: Array, default: () => [] },
			nextEstablishmentCode: { type: String, default: null },
			activityLimit: { type: Number, default: null },
			activityRemaining: { type: Number, default: null },
			loaded: { type: Boolean, default: false },
		},
		// `fix-establishment`: abrir en la tarjeta de establecimientos el que no tiene dirección.
		emits: ['updated', 'fix-establishment'],
		data() {
			return {
				NEW_ESTABLISHMENT,
				dialog: false,
				editing: null,
				name: '',
				choice: null,
				draft: this.blankDraft(),
				logoFile: null,      // el logo ya recortado a 950 × 400, el que se sube
				logoPreview: null,
				logoSource: null,    // la imagen original, para volver a ajustarla
				cropperVisible: false,
				errors: {},
				formError: null,
				saving: false,
			}
		},
		computed: {
			activitiesFull() {
				return this.activityLimit !== null && this.activityRemaining !== null && this.activityRemaining <= 0;
			},
			options() {
				return pointOptions(this.establishments, this.editing ? this.editing.id : null);
			},
			chosenPointId() {
				return this.choice && this.choice.startsWith('pt:') ? parseInt(this.choice.slice(3), 10) : null;
			},
			chosen() {
				return this.chosenPointId ? findPoint(this.establishments, this.chosenPointId) : null;
			},
			shared() {
				return this.chosenPointId ? sharedWith(this.establishments, this.chosenPointId, this.editing ? this.editing.id : null) : [];
			},
			// Al editar, cambiar de punto mueve la numeración de sus próximas facturas.
			movesNumbering() {
				return !!(this.editing && this.chosen && this.editing.emitionPoint && this.chosen.point.id !== this.editing.emitionPoint.id);
			},
			newPointIn() {
				if (!this.choice || !this.choice.startsWith('np:')) { return null }
				const id = parseInt(this.choice.slice(3), 10);
				return this.establishments.find(e => e.id === id) || null;
			},
			logoActivity() {
				return { name: this.name || (this.editing && this.editing.name) || '', logoUrl: this.editing ? this.editing.logoUrl : null };
			},
		},
		watch: {
			// Al cambiar de opción se proponen los códigos siguientes y se limpian sus errores.
			choice(value) {
				const keep = this.errors.name ? { name: this.errors.name } : {};
				this.errors = keep;
				if (this.newPointIn) {
					this.draft = Object.assign(this.blankDraft(), { pointCode: this.newPointIn.nextPointCode || '' });
				} else if (value === NEW_ESTABLISHMENT) {
					this.draft = Object.assign(this.blankDraft(), { code: this.nextEstablishmentCode || '' });
				}
			},
		},
		methods: {
			hasAddress,
			blankDraft() {
				return { code: '', pointCode: '001', name: '', address: '', seq: emptySequentials() };
			},
			sourceOf(activity) {
				return activity.emitionPoint ? findPoint(this.establishments, activity.emitionPoint.id) : null;
			},
			openNew() {
				if (this.activitiesFull) {
					this.$toast.add({severity: 'warn', summary: 'Sin cupo de actividades', life: 6000,
						detail: 'Alcanzaste el número de actividades de tu plan. Agrega una por $15 desde Suscripción.'});
					return;
				}
				this.reset();
				this.dialog = true;
			},
			openEdit(activity) {
				this.reset();
				this.editing = activity;
				this.name = activity.name || '';
				this.choice = activity.emitionPoint ? pointValue(activity.emitionPoint.id) : null;
				this.dialog = true;
			},
			reset() {
				this.editing = null;
				this.name = '';
				this.choice = null;
				this.draft = this.blankDraft();
				this.logoFile = null;
				if (this.logoPreview) { URL.revokeObjectURL(this.logoPreview) }
				this.logoPreview = null;
				this.logoSource = null;
				this.cropperVisible = false;
				this.errors = {};
				this.formError = null;
				this.saving = false;
			},
			fixEstablishment(establishment) {
				this.dialog = false;
				this.$emit('fix-establishment', establishment.id);
			},
			validate() {
				let errors = {};
				if (!this.name.trim()) { errors.name = 'Escribe el nombre comercial.' }
				if (!this.choice) {
					errors.point = 'Elige desde qué punto de emisión factura esta actividad.';
				} else if (this.newPointIn) {
					errors = Object.assign(errors, validateNewPoint(this.draft, this.newPointIn));
				} else if (this.choice === NEW_ESTABLISHMENT) {
					errors = Object.assign(errors, validateNewEstablishment(this.draft, this.establishments));
				}
				return errors;
			},
			async save() {
				this.formError = null;
				this.errors = this.validate();
				if (Object.keys(this.errors).length) { return }

				const form = new FormData();
				if (this.editing) { form.append('id', this.editing.id) }
				form.append('name', this.name.trim());
				if (this.logoFile) { form.append('logoFile', this.logoFile) }
				Object.entries(activityPointFields(this.choice, this.draft)).forEach(([key, value]) => {
					if (value !== null && value !== undefined) { form.append(key, value) }
				});

				this.saving = true;
				const response = await httpRequest.saveEconActivity(form);
				this.saving = false;
				if (response && response.status === 200) {
					const isNew = !this.editing;
					const name = this.name.trim();
					this.$emit('updated', response.data);
					this.dialog = false;
					this.$toast.add({severity: 'success', summary: 'Guardado', life: 4000,
						detail: isNew ? `«${name}» ya puede facturar.` : 'Los datos de la actividad se actualizaron.'});
					return;
				}
				const field = serverField(response);
				if (field && field !== 'startSequentials') {
					this.errors = Object.assign({}, this.errors, {[field]: response.error});
				} else if (response && response.status === 422) {
					// Cupo agotado u otra regla del plan (p. ej. ofrecer el add-on de $15).
					this.formError = response.error || 'Revisa los datos e inténtalo de nuevo.';
				} else {
					this.formError = (response && response.error) || NETWORK_ERROR;
				}
			},
			// El logo se ajusta a 950 × 400 px en el recortador antes de subirlo.
			onLogoChanged(event) {
				const original = event.target.files && event.target.files[0];
				event.target.value = '';
				if (!original) { return }
				if (!original.type || original.type.indexOf('image/') !== 0) {
					this.errors = Object.assign({}, this.errors, {logoFile: 'Elige una imagen PNG o JPG.'});
					return;
				}
				const errors = Object.assign({}, this.errors);
				delete errors.logoFile;
				this.errors = errors;
				this.logoSource = original;
				this.cropperVisible = true;
			},
			onCropped(file) {
				this.logoFile = file;
				if (this.logoPreview) { URL.revokeObjectURL(this.logoPreview) }
				this.logoPreview = URL.createObjectURL(file);
				this.cropperVisible = false;
			},
		},
	}
</script>

<style scoped>
	.section-header { margin-bottom: 1.25rem; }
	.section-header-action {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}
	.section-header-action > div { flex: 1 1 16rem; min-width: 0; }
	.section-title { font-size: 1.25rem; font-weight: 600; margin: 0 0 0.25rem 0; color: inherit; }
	.section-desc { margin: 0; line-height: 1.5; color: var(--text-color-secondary); }
	.header-button { width: auto; flex-shrink: 0; }
	.quota { display: block; margin: -0.5rem 0 1rem; color: var(--text-color-secondary); }
	.empty { color: var(--text-color-secondary); }
	.field-label { display: block; font-weight: 500; margin-bottom: 0.5rem; color: var(--text-color); }
	.field-help,
	.field-error { display: block; margin-top: 0.375rem; font-size: 0.9rem; line-height: 1.4; }
	.field-help { color: var(--text-color-secondary); }
	.field-error { font-weight: 500; color: #B91C1C; }
	.series {
		font-family: ui-monospace, Menlo, Consolas, monospace;
		font-variant-numeric: tabular-nums;
		font-weight: 600;
		color: var(--text-color);
	}

	.act-list { list-style: none; margin: 0; padding: 0; }
	.act {
		display: grid;
		grid-template-columns: auto 1fr auto;
		gap: 1rem;
		align-items: center;
		padding: 0.9rem 0;
		border-top: 1px solid var(--surface-border);
	}
	.act:first-child { border-top: 0; padding-top: 0; }
	/* El logo es de 950 × 400: su hueco tiene esa proporción; sin logo, las iniciales al centro. */
	.act-logo-slot {
		width: 95px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.act-logo-slot :deep(.act-logo) { width: 95px; height: 40px; object-fit: contain; }
	.act-body { min-width: 0; }
	.act-name { font-weight: 600; color: var(--text-color); }
	.act-from { font-size: 0.9rem; color: var(--text-color-secondary); overflow-wrap: anywhere; }
	.no-address { color: #B91C1C; font-weight: 500; }

	.subform {
		border: 1px solid var(--surface-border);
		background: var(--surface-ground);
		border-radius: 10px;
		padding: 1rem;
		margin-bottom: 1rem;
	}
	.subform-title { margin: 0 0 0.75rem; font-weight: 600; color: var(--text-color); }
	.inline-link {
		padding: 0;
		border: 0;
		background: none;
		font: inherit;
		font-weight: 600;
		color: inherit;
		text-decoration: underline;
		cursor: pointer;
	}
	.logo-pick { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
	.logo-pick .p-button { width: auto; }
	.logo-actions { display: flex; flex-wrap: wrap; gap: 0.25rem; }
	/* Marco de 950 × 400 a escala, con damero: se ve qué queda transparente. */
	.logo-frame {
		width: 190px;
		height: 80px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		border: 1px solid var(--surface-border);
		border-radius: 6px;
		overflow: hidden;
		background-color: #ffffff;
		background-image:
			linear-gradient(45deg, #eef0f3 25%, transparent 25%),
			linear-gradient(-45deg, #eef0f3 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, #eef0f3 75%),
			linear-gradient(-45deg, transparent 75%, #eef0f3 75%);
		background-size: 16px 16px;
		background-position: 0 0, 0 8px, 8px -8px, -8px 0;
	}
	.logo-frame :deep(.logo-preview),
	.logo-frame .logo-preview { width: 190px; height: 80px; object-fit: contain; }
</style>
